//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import FacebookIcon from '@material-ui/icons/Facebook';
import InstagramIcon from '@material-ui/icons/Instagram';
import WebsiteIcon from '@material-ui/icons/Language';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import YoutubeIcon from '@material-ui/icons/YouTube';
import GitHubIcon from '@material-ui/icons/GitHub';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import {
    PROFILE_SOCIAL_MEDIA,
    PROFILE_SOCIAL_MEDIA_FACEBOOK,
    PROFILE_SOCIAL_MEDIA_LINKEDIN,
    PROFILE_SOCIAL_MEDIA_YOUTUBE,
    PROFILE_SOCIAL_MEDIA_INSTAGRAM,
    PROFILE_SOCIAL_MEDIA_WEBSITE,
    PROFILE_SOCIAL_MEDIA_TIKTOK,
    PROFILE_SOCIAL_MEDIA_GITHUB,
    PROFILE_SOCIAL_MEDIA_X,
    CONFIRM_DIALOG_MESSAGE_CHANGE_EMP_PROFILE_TO_DRAFT,
    CONFIRM_DIALOG_MESSAGE_CHANGE_JS_PROFILE_TO_DRAFT,
} from '../../common/constants';
import { changeEmpProfileToDraft } from '../../common/services/empMyProfileServices';
import TiktokIcon from '../../assets/tiktok_icon.png';
import XIcon from '../../assets/x_logo_black.png';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { changeProfileDraft } from '../../common/services/myProfileServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdLink from '../../common/elemental/AfitdLink';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import SocialMediaPortfolioDialog from './SocialMediaPortfolioDialog';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { data } = params;

    const socialMediaBlockUrlStyles = {
        fontFamily: 'Roboto, sans-serif',
        fontSize: '14px',
        lineHeight: '16px',
        // color: '#9B9E9C',
        // color: `${theme.palette.secondary.main}`,
        color: '#1D8A8B',
        fontWeight: '500',
        padding: '0 0 0 15px',
        // margin: '13px 0 0 0',
    };

    const socialMediaBlockUrlPlaceHolderStyles = {
        fontFamily: 'Roboto, sans-serif',
        fontSize: '14px',
        lineHeight: '16px',
        color: 'rgba(17, 30, 20, 0.4)',
        fontWeight: '500',
        padding: '5px 21px 0px 15px',
    };

    const facebookUrlStyles = _isEmpty(data.facebookUrl) ? socialMediaBlockUrlPlaceHolderStyles : socialMediaBlockUrlStyles;
    const linkedInUrlStyles = _isEmpty(data.linkedInUrl) ? socialMediaBlockUrlPlaceHolderStyles : socialMediaBlockUrlStyles;
    const youTubeUrlStyles = _isEmpty(data.youTubeUrl) ? socialMediaBlockUrlPlaceHolderStyles : socialMediaBlockUrlStyles;
    const instagramUrlStyles = _isEmpty(data.instagramUrl) ? socialMediaBlockUrlPlaceHolderStyles : socialMediaBlockUrlStyles;
    const xUrlStyles = _isEmpty(data.twitterUrl) ? { ...socialMediaBlockUrlPlaceHolderStyles, paddingLeft: 22 } : { ...socialMediaBlockUrlStyles, paddingLeft: 22 };
    const websiteUrlStyles = _isEmpty(data.websiteUrl) ? socialMediaBlockUrlPlaceHolderStyles : socialMediaBlockUrlStyles;
    const tikTokUrlStyles = _isEmpty(data.tikTokUrl) ? { ...socialMediaBlockUrlPlaceHolderStyles, paddingLeft: 22 } : { ...socialMediaBlockUrlStyles, paddingLeft: 22 };
    const gitHubUrlStyles = _isEmpty(data.gitHubUrl) ? socialMediaBlockUrlPlaceHolderStyles : socialMediaBlockUrlStyles;

    const socialMediaBlockTitleStyles = {
        fontFamily: 'Roboto, sans-serif',
        fontSize: '16px',
        lineHeight: '19px',
        color: '#111E14',
        fontWeight: '600',
        padding: '15px 21px 0px 15px',
    };

    return makeStyles(theme => ({
        root: {
            backgroundColor: '#FFFFFF',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
        },
        titleContainer: {
            padding: '0 0 19px 0',
        },
        editButtonContainer: {
            fontSize: '16px',
            // textAlign: 'right',
        },
        socialMediaBlock: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            borderRadius: '4px',
            margin: '13px 0 0 0',
            padding: '0 0 10px 0',
            border: '1px solid #D5D7DD',
        },
        socialMediaBlockTitle: socialMediaBlockTitleStyles,
        socialMediaBlockTitleTikTok: { ...socialMediaBlockTitleStyles, paddingLeft: 20 },
        socialMediaBlockTitleX: { ...socialMediaBlockTitleStyles, paddingLeft: 22 },
        iconsContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
        icons: {
            fontSize: '46px',
            // padding: '12px',
            // paddingBottom: '0px',
            padding: '0px',
            paddingTop: 8,
            margin: '0px 8px',
        },
        iconContainer: {
            // display: 'flex',
            // marginTop: 10,
            // marginLeft: 5,
            // alignContent: 'center',
            // alignItems: 'center',
            // justifyContent: 'center',
        },
        facebookUrl: { ...facebookUrlStyles },
        linkedInUrl: { ...linkedInUrlStyles },
        youTubeUrl: { ...youTubeUrlStyles },
        instagramUrl: { ...instagramUrlStyles },
        xUrl: { ...xUrlStyles },
        websiteUrl: { ...websiteUrlStyles },
        tikTokUrl: { ...tikTokUrlStyles },
        gitHubUrl: { ...gitHubUrlStyles },
        // editLink: {
        //     fontSize: '16px',
        // },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, props, editDialogOpen, setEditDialogOpen, makeProfilePublicDialog, setMakePofilePublicDialog, onConfrimChangeProfileType } = params;
    const { data = {}, isEmp } = props;

    // const isProfilePublic = _get(data, 'isPublic');

    const defns = {
        titleText: {
            variant: 'h6',
            clsLightBold: true,
            value: PROFILE_SOCIAL_MEDIA,
        },
        facebookUrl: {
            value: 'Not Connected',
            label: data.facebookUrl,
            onClick: handleClickUrlLink.bind(null, data.facebookUrl),
        },
        linkedInUrl: {
            value: 'Not Connected',
            label: data.linkedInUrl,
            onClick: handleClickUrlLink.bind(null, data.linkedInUrl),
        },
        youTubeUrl: {
            value: 'Not Connected',
            label: data.youTubeUrl,
            onClick: handleClickUrlLink.bind(null, data.youTubeUrl),
        },
        instagramUrl: {
            value: 'Not Connected',
            label: data.instagramUrl,
            onClick: handleClickUrlLink.bind(null, data.instagramUrl),
        },
        xUrl: {
            value: 'Not Connected',
            label: data.twitterUrl,
            onClick: handleClickUrlLink.bind(null, data.twitterUrl),
        },
        websiteUrl: {
            value: 'Not Connected',
            label: data.websiteUrl,
            onClick: handleClickUrlLink.bind(null, data.websiteUrl),
        },
        tikTokUrl: {
            value: 'Not Connected',
            label: data.tikTokUrl,
            onClick: handleClickUrlLink.bind(null, data.tikTokUrl),
        },
        gitHubUrl: {
            value: 'Not Connected',
            label: data.gitHubUrl,
            onClick: handleClickUrlLink.bind(null, data.gitHubUrl),
        },
        // editLink: {
        //     label: 'Edit',
        //     onClick: () => {
        //         if (isEmp) {
        //             if (isEmpProfilePublic) setMakePofilePublicDialog(true);
        //             else setEditDialogOpen(true);
        //         } else {
        //             if (isProfilePublic) setMakePofilePublicDialog(true);
        //             else setEditDialogOpen(true);
        //         }
        //     },
        // },
        editDialog: {
            data: data,
            open: editDialogOpen,
            onClose: handleCloseEditDialog.bind(null, setEditDialogOpen),
        },
        makeProfilePublicDialog: {
            maxWidth: 'sm',
            title: 'Edit Your Profile?',
            onClose: () => setMakePofilePublicDialog(false),
            onConfirm: onConfrimChangeProfileType,
            open: makeProfilePublicDialog,
            message: isEmp ? CONFIRM_DIALOG_MESSAGE_CHANGE_EMP_PROFILE_TO_DRAFT : CONFIRM_DIALOG_MESSAGE_CHANGE_JS_PROFILE_TO_DRAFT,
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
// const handleClickEditLink = (setEditDialogOpen, evt) => {
//     setEditDialogOpen(true);
// };

const handleCloseEditDialog = (setEditDialogOpen, evt) => {
    setEditDialogOpen(false);
};

const handleClickUrlLink = (url, evt) => {
    if (!_isEmpty(url)) {
        window.open(url, '_blank');
    }
};

const UrlLink = props => {
    if (_isEmpty(props.label)) {
        return <AfitdTypography {...props} />;
    } else {
        return <AfitdLink {...props} />;
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const SocialMediaPortfolio = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const { userId, employerId } = useContext(AppContext);

    const { setSnackbarMessage, setSnackbarErrorMessage } = useContext(LayoutContext);

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [makeProfilePublicDialog, setMakePofilePublicDialog] = useState(false);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { data = {}, editDialogOpen, setEditDialogOpen, isEmp } = props;

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Handlers
    //---------------------------------------------------------------------
    const onConfrimChangeProfileType = async () => {
        if (isEmp) await changeEmpProfileToDraft({ employerId, setSnackbarMessage, setSnackbarErrorMessage });
        else await changeProfileDraft({ userId, setSnackbarMessage, setSnackbarErrorMessage });
    };

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const classes = useStyles({ data })();
    const defns = getDefns({ classes, props, editDialogOpen, setEditDialogOpen, makeProfilePublicDialog, setMakePofilePublicDialog, onConfrimChangeProfileType });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div {...defns.header}>
                <div {...defns.titleContainer}>
                    <AfitdTypography {...defns.titleText} />
                </div>
                {/* <div {...defns.editButtonContainer}>
                    <AfitdLink {...defns.editLink} />
                </div> */}
            </div>
            <div {...defns.socialMediaBlock}>
                <div {...defns.iconsContainer}>
                    <FacebookIcon {...defns.icons} />
                </div>
                <div>
                    <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_FACEBOOK}</AfitdTypography>
                    <UrlLink {...defns.facebookUrl} />
                </div>
            </div>
            <div {...defns.socialMediaBlock}>
                <div {...defns.iconsContainer}>
                    <LinkedInIcon {...defns.icons} />
                </div>
                <div>
                    <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_LINKEDIN}</AfitdTypography>
                    <UrlLink {...defns.linkedInUrl} />
                </div>
            </div>
            <div {...defns.socialMediaBlock}>
                <div {...defns.iconsContainer}>
                    <InstagramIcon {...defns.icons} />
                </div>
                <div>
                    <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_INSTAGRAM}</AfitdTypography>
                    <UrlLink {...defns.instagramUrl} />
                </div>
            </div>
            <div {...defns.socialMediaBlock}>
                <div {...defns.iconsContainer}>
                    &nbsp;&nbsp;&nbsp; <img src={XIcon} alt="x_icon" width="28px" style={{ marginTop: '10px', marginLeft: 6 }} /> &nbsp;&nbsp;
                </div>
                <div>
                    <AfitdTypography {...defns.socialMediaBlockTitleX}>{PROFILE_SOCIAL_MEDIA_X}</AfitdTypography>
                    <UrlLink {...defns.xUrl} />
                </div>
            </div>
            <div {...defns.socialMediaBlock}>
                <div {...defns.iconsContainer}>
                    <YoutubeIcon {...defns.icons} />
                </div>
                <div>
                    <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_YOUTUBE}</AfitdTypography>
                    <UrlLink {...defns.youTubeUrl} />
                </div>
            </div>
            <div {...defns.socialMediaBlock}>
                <div {...defns.iconsContainer}>
                    &nbsp;&nbsp;&nbsp; <img src={TiktokIcon} alt="tiktok_icon" width="37px" style={{ marginTop: '10px' }} /> &nbsp;&nbsp;
                </div>
                <div>
                    <AfitdTypography {...defns.socialMediaBlockTitleTikTok}>{PROFILE_SOCIAL_MEDIA_TIKTOK}</AfitdTypography>
                    <UrlLink {...defns.tikTokUrl} />
                </div>
            </div>
            <div {...defns.socialMediaBlock}>
                <div {...defns.iconsContainer}>
                    <GitHubIcon {...defns.icons} />
                </div>
                <div>
                    <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_GITHUB}</AfitdTypography>
                    <UrlLink {...defns.gitHubUrl} />
                </div>
            </div>
            <div {...defns.socialMediaBlock}>
                <div {...defns.iconsContainer}>
                    <WebsiteIcon {...defns.icons} />
                </div>
                <div>
                    <AfitdTypography {...defns.socialMediaBlockTitle}>{PROFILE_SOCIAL_MEDIA_WEBSITE}</AfitdTypography>
                    <UrlLink {...defns.websiteUrl} />
                </div>
            </div>
            <AfitdConfirmDialog {...defns.makeProfilePublicDialog} />
            {editDialogOpen && <SocialMediaPortfolioDialog {...defns.editDialog} />}
        </div>
    );
};

export default SocialMediaPortfolio;
